import React from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Utils from '../../Utils/Utils';

// ── Color tokens ──────────────────────────────────────────────────────
const C = {
  gold: '#D4AF37',
  goldLight: '#f5ecd0',
  navy: '#0f1847',
  navyA: '#0a0e2b',
  navyB: '#111b5e',
  green: '#16a34a',
  greenBg: '#dcfce7',
  red: '#dc2626',
  redBg: '#fee2e2',
  amber: '#d97706',
  amberBg: '#fef3c7',
  bg: '#f4f5f8',
  card: '#ffffff',
  border: '#e5e7eb',
  muted: '#6b7280',
  mutedLight: '#8a9cc5',
  text: '#0f172a',
};

// Current tier names, then the pre-rename ones so an un-migrated row
// still renders. Premium keeps the top-tier indigo it had as "Pro Elite";
// Regular inherits the green the old middle-tier "Premium" used.
const PLAN_BADGE = {
  Premium: { bg: '#eef2ff', color: '#4338ca', border: '#c7d2fe' },
  Regular: { bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
  Basic: { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
  Alpha: { bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe' },
  // legacy names (pre-rename ladder)
  'Pro Elite': { bg: '#eef2ff', color: '#4338ca', border: '#c7d2fe' },
  Pro: { bg: '#eef2ff', color: '#4338ca', border: '#c7d2fe' },
  Standard: { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
};

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const STATUS_ORDER = { request: 0, unpaid: 1, paid: 2 };

const fmtK = (v) => (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v}`);
const fmtUSD = (v) => `$${Number(v).toLocaleString()}`;

// Map raw paymentMethod strings persisted on LicensePayment to admin-
// facing labels. Stripe paths store brand + last4 inside paymentDetails,
// so prefer "Visa •••• 4242" when we have it.
function formatPaymentMethod(row) {
  const method = row?.method;
  if (!method) return 'Not set';
  const details = row?.paymentDetails || {};
  if (method === 'cash_zelle') return 'Cash / Zelle';
  if (method === 'pending') return 'Collect Later';
  if (method === 'apple_pay') return 'Apple Pay';
  if (method === 'google_pay') return 'Google Pay';
  if (method === 'link')
    return details.linkEmail ? `Link · ${details.linkEmail}` : 'Link';
  if (method === 'stripe_card' || method === 'card') {
    const brand = details.cardBrand || details.brand;
    const last4 = details.cardLast4 || details.last4;
    const brandLabel = brand
      ? brand.charAt(0).toUpperCase() + brand.slice(1)
      : 'Card';
    return last4 ? `${brandLabel} •••• ${last4}` : brandLabel;
  }
  if (method === 'bank') return 'Bank (ACH)';
  return method;
}

// What a location actually owes for a given month.
//
// `row.rate` is only a PROJECTION — the flat monthly price for months that
// have no invoice yet. Wherever a real LicensePayment row exists the server
// puts its amount in `row.due[month]`, and that wins: a first post-trial
// cycle carrying an unused-trial credit is genuinely $420, not the $700
// rate, and the tracker must not contradict the payment history, the Pay
// button and the Stripe charge.
//
// Falls back to `rate` for older payloads that predate `due`.
function dueForMonth(row, monthIdx) {
  const d = row?.due;
  if (Array.isArray(d) && Number.isFinite(Number(d[monthIdx]))) {
    return Number(d[monthIdx]);
  }
  return row?.rate || 0;
}

function deriveStatus(row, proj, coll) {
  if (proj === 0 && coll === 0) return 'paid';
  if (coll >= proj && proj > 0) return 'paid';
  if (
    row.method === 'cash_zelle' &&
    row.zelle &&
    Number(row.zelle.sent || 0) >= proj &&
    row.collectionStatus !== 'collected'
  )
    return 'request';
  if (row.method === 'pending' && row.collectionStatus === 'pending')
    return 'request';
  return 'unpaid';
}

const STATUS_LABEL = {
  paid: { label: 'PAID', color: C.green, bg: C.greenBg },
  request: { label: 'REVIEW', color: C.amber, bg: C.amberBg },
  unpaid: { label: 'UNPAID', color: C.red, bg: C.redBg },
};

// ── Component ─────────────────────────────────────────────────────────
class MembershipSummary extends React.Component {
  constructor(props) {
    super(props);
    const now = new Date();
    this.state = {
      year: now.getFullYear(),
      periodMode: 'monthly',
      monthIdx: now.getMonth(),
      quarter: Math.floor(now.getMonth() / 3),
      groupFilter: 'all',
      statusFilter: 'all',
      search: '',
      sortBy: 'status',
      sortMenuOpen: false,
      expandedId: null,
      verified: null,
      visibleCount: 20,
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  fetchData = () => {
    if (this.props.getCompanyMemberships) {
      this.props.getCompanyMemberships(this.state.year);
    }
  };

  changeYear = (year) => {
    this.setState({ year }, this.fetchData);
  };

  // ── Computations ────────────────────────────────────────────────────
  getPeriodMonths() {
    const { periodMode, monthIdx, quarter } = this.state;
    if (periodMode === 'monthly') return [monthIdx];
    if (periodMode === 'quarterly') {
      const s = quarter * 3;
      return [s, s + 1, s + 2];
    }
    return Array.from({ length: 12 }, (_, i) => i);
  }

  calcRow(row) {
    const pMonths = this.getPeriodMonths();
    const { year } = this.state;

    // Closed companies are no longer expected to pay — $0 projected.
    const isClosed = row.status === 'closed';

    // Only charge from the month/year the company was created.
    // Months before creation have $0 due.
    let proj = 0;
    if (!isClosed) {
      pMonths.forEach((m) => {
        const beforeCreation =
          row.startYear > 0 &&
          (year < row.startYear ||
            (year === row.startYear && m < row.startMonth));
        const isTrial = row.trialMonth === m && row.trialYear === year;
        proj += beforeCreation || isTrial ? 0 : dueForMonth(row, m);
      });
    }

    const coll = pMonths.reduce((s, i) => s + (row.pay[i] || 0), 0);
    const bal = proj - coll;
    const st = isClosed ? 'paid' : deriveStatus(row, proj, coll);
    return { proj, coll, bal, st, isClosed };
  }

  getFilteredRows() {
    const data = this.props.memberships || [];
    const { groupFilter, statusFilter, search, sortBy } = this.state;
    let rows = data.map((r) => ({ ...r, ...this.calcRow(r) }));
    if (groupFilter !== 'all')
      rows = rows.filter((r) => r.group === groupFilter);
    if (statusFilter !== 'all')
      rows = rows.filter((r) => r.st === statusFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (r) => r.name.toLowerCase().includes(q) || (r.id || '').includes(q)
      );
    }
    if (sortBy === 'status')
      rows.sort((a, b) => STATUS_ORDER[a.st] - STATUS_ORDER[b.st]);
    else if (sortBy === 'balance') rows.sort((a, b) => b.bal - a.bal);
    else if (sortBy === 'amount') rows.sort((a, b) => b.proj - a.proj);
    else if (sortBy === 'name')
      rows.sort((a, b) => a.name.localeCompare(b.name));
    return rows;
  }

  handleVerify = (row) => {
    // Verify-and-activate flow used by the Zelle / Collect-Later panels.
    // These panels are only shown when the latest payment row IS the
    // row being acted on, so updateLicensePayment with latestPaymentId
    // hits the right record. The Unpaid panel uses handleMarkPaid below
    // which is period-targeted and handles the "no row yet" case.
    this.setState({ verified: row.companyId });
    if (this.props.updateLicensePayment && row.latestPaymentId) {
      this.props.updateLicensePayment(
        {
          paymentId: row.latestPaymentId,
          companyId: row.companyId,
          collectionStatus: 'collected',
        },
        () => {
          setTimeout(() => {
            this.setState({ verified: null, expandedId: null });
            this.fetchData();
          }, 600);
        }
      );
    }
  };

  // Period-targeted Mark Paid. Resolves to the calendar month the admin
  // is currently viewing — works for past months (no latest payment row
  // covers them yet), the current month, and upcoming months. Disabled
  // in quarterly / yearly modes because the action needs a single month
  // to anchor coversCycleEndDate; the hint guides the admin to switch.
  handleMarkPaid = (row) => {
    if (!this.props.markCyclePaid) return;
    if (this.state.periodMode !== 'monthly') return;
    const { year, monthIdx } = this.state;
    this.setState({ verified: row.companyId });
    this.props.markCyclePaid(
      {
        companyId: row.companyId,
        year,
        month: monthIdx,
        // The month's real invoice, not the flat rate — marking a
        // credit-reduced cycle paid must record what was actually owed.
        amount: dueForMonth(row, monthIdx),
      },
      (success) => {
        setTimeout(() => {
          this.setState({
            verified: success ? row.companyId : null,
            expandedId: success ? null : this.state.expandedId,
          });
          if (success) this.fetchData();
        }, 600);
      }
    );
  };

  // ── PDF Export ───────────────────────────────────────────────────────

  downloadPDF = () => {
    const rows = this.getFilteredRows();
    const { year, periodMode, monthIdx, quarter } = this.state;

    // Build period label
    let periodLabel = `${year}`;
    if (periodMode === 'monthly') periodLabel = `${MONTHS[monthIdx]} ${year}`;
    else if (periodMode === 'quarterly')
      periodLabel = `Q${quarter + 1} ${year}`;

    // KPI totals
    const projected = rows.reduce((s, r) => s + r.proj, 0);
    const collected = rows.reduce((s, r) => s + r.coll, 0);
    const outstanding = projected - collected;
    const rate = projected > 0 ? Math.round((collected / projected) * 100) : 0;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const today = new Date().toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    // Header
    doc.setFillColor(15, 24, 71);
    doc.rect(0, 0, pageWidth, 28, 'F');
    doc.setTextColor(212, 175, 55);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('MEMBERSHIP REPORT', 14, 14);
    doc.setFontSize(9);
    doc.setTextColor(148, 163, 184);
    doc.text(`Period: ${periodLabel}  |  Generated: ${today}`, 14, 22);

    // KPI summary
    let y = 36;
    doc.setTextColor(15, 24, 71);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text(`Projected: $${projected.toLocaleString()}`, 14, y);
    doc.setTextColor(22, 163, 74);
    doc.text(`Collected: $${collected.toLocaleString()}`, 80, y);
    doc.setTextColor(220, 38, 38);
    doc.text(`Outstanding: $${outstanding.toLocaleString()}`, 146, y);
    y += 6;
    doc.setTextColor(107, 114, 128);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text(`Collection Rate: ${rate}%  |  ${rows.length} locations`, 14, y);

    // Table
    const tableRows = rows.map((r) => [
      r.id,
      r.isClosed ? `${r.name} (CLOSED)` : r.name,
      r.plan || '—',
      r.group || '—',
      `$${r.proj.toLocaleString()}`,
      `$${r.coll.toLocaleString()}`,
      `$${r.bal.toLocaleString()}`,
      r.isClosed ? 'CLOSED' : r.st.toUpperCase(),
    ]);

    autoTable(doc, {
      startY: y + 6,
      head: [
        ['ID', 'Location', 'Plan', 'Group', 'Due', 'Paid', 'Balance', 'Status'],
      ],
      body: tableRows,
      theme: 'grid',
      headStyles: {
        fillColor: [15, 24, 71],
        textColor: [212, 175, 55],
        fontStyle: 'bold',
        fontSize: 8,
      },
      bodyStyles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' },
        7: { halign: 'center', cellWidth: 18 },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 7) {
          const val = data.cell.raw;
          if (val === 'PAID') data.cell.styles.textColor = [22, 163, 74];
          else if (val === 'REVIEW') data.cell.styles.textColor = [217, 119, 6];
          else if (val === 'UNPAID') data.cell.styles.textColor = [220, 38, 38];
          else if (val === 'CLOSED')
            data.cell.styles.textColor = [107, 114, 128];
        }
        if (data.section === 'body' && data.column.index === 5) {
          const st = tableRows[data.row.index]?.[7];
          if (st === 'PAID') data.cell.styles.textColor = [22, 163, 74];
          else if (st === 'REVIEW') data.cell.styles.textColor = [217, 119, 6];
          else if (st === 'UNPAID') data.cell.styles.textColor = [220, 38, 38];
        }
      },
    });

    // Footer
    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text('NuVe System — Membership Report', 14, finalY);
    doc.text(`Page 1`, pageWidth - 24, finalY);

    const pdfData = doc.output('arraybuffer');
    const fileName = `Membership_Report_${periodLabel.replace(/\s/g, '_')}.pdf`;
    Utils.handlePdfAction(pdfData, 'download', fileName);
  };

  // ── Render helpers ──────────────────────────────────────────────────

  renderYearChips() {
    const { year } = this.state;
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = 2025; y <= currentYear + 1; y++) years.push(y);

    return (
      <div
        style={{
          display: 'flex',
          gap: 6,
          padding: '14px 24px 0',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {years.map((y) => {
          const active = year === y;
          return (
            <button
              key={y}
              onClick={() => this.changeYear(y)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '5px 14px',
                borderRadius: 20,
                border: active ? 'none' : `1px solid ${C.border}`,
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                background: active ? C.navy : '#f1f5f9',
                color: active ? C.gold : C.muted,
                transition: 'all .15s',
              }}
            >
              {y}
            </button>
          );
        })}
      </div>
    );
  }

  renderPeriodSelector() {
    const { periodMode, monthIdx, quarter } = this.state;
    const modes = [
      { id: 'monthly', label: 'Monthly' },
      { id: 'quarterly', label: 'Quarterly' },
      { id: 'yearly', label: 'Yearly' },
    ];

    // Quarter collection rates
    const allData = this.props.memberships || [];
    const qPct = [0, 1, 2, 3].map((q) => {
      const ms = [q * 3, q * 3 + 1, q * 3 + 2];
      let proj = 0,
        coll = 0;
      allData.forEach((r) => {
        proj += ms.reduce((s, i) => s + dueForMonth(r, i), 0);
        coll += ms.reduce((s, i) => s + (r.pay[i] || 0), 0);
      });
      return proj > 0 ? Math.round((coll / proj) * 100) : 0;
    });

    return (
      <div
        style={{ padding: '16px 24px', borderBottom: `1px solid ${C.border}` }}
      >
        {/* Mode toggle */}
        <div
          style={{
            display: 'inline-flex',
            gap: 6,
            background: '#f1f5f9',
            borderRadius: 8,
            padding: 3,
            marginBottom: 12,
          }}
        >
          {modes.map((m) => {
            const active = periodMode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => this.setState({ periodMode: m.id })}
                style={{
                  padding: '6px 18px',
                  borderRadius: 6,
                  border: active
                    ? `2px solid ${C.gold}`
                    : `1px solid ${C.border}`,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: active ? C.goldLight : C.card,
                  color: active ? C.navy : C.muted,
                  transition: 'all .15s',
                }}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        {/* Monthly sub-selector */}
        {periodMode === 'monthly' && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {MONTHS.map((m, i) => {
              const active = monthIdx === i;
              return (
                <button
                  key={i}
                  onClick={() => this.setState({ monthIdx: i })}
                  style={{
                    padding: '5px 10px',
                    borderRadius: 6,
                    border: active
                      ? `2px solid ${C.gold}`
                      : `1px solid ${C.border}`,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: active ? C.goldLight : C.card,
                    color: active ? C.navy : C.muted,
                    transition: 'all .15s',
                  }}
                >
                  {m}
                </button>
              );
            })}
          </div>
        )}

        {/* Quarterly sub-selector */}
        {periodMode === 'quarterly' && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              gap: 8,
            }}
          >
            {[0, 1, 2, 3].map((q) => {
              const active = quarter === q;
              const pct = qPct[q];
              const pctColor =
                pct >= 80 ? C.green : pct >= 50 ? C.amber : C.red;
              return (
                <button
                  key={q}
                  onClick={() => this.setState({ quarter: q })}
                  style={{
                    padding: '10px 0',
                    borderRadius: 8,
                    border: active
                      ? `2px solid ${C.gold}`
                      : `1px solid ${C.border}`,
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: active ? C.goldLight : C.card,
                    color: C.navy,
                    textAlign: 'center',
                    transition: 'all .15s',
                  }}
                >
                  Q{q + 1}
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: pctColor,
                      marginTop: 2,
                    }}
                  >
                    {pct}%
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  renderKPIs() {
    const rows = this.getFilteredRows();
    const projected = rows.reduce((s, r) => s + r.proj, 0);
    const collected = rows.reduce((s, r) => s + r.coll, 0);
    const outstanding = projected - collected;
    const rate = projected > 0 ? Math.round((collected / projected) * 100) : 0;
    const rateColor = rate >= 80 ? C.green : rate >= 50 ? C.amber : C.red;

    const cards = [
      {
        label: 'PROJECTED',
        value: fmtK(projected),
        sub: fmtUSD(projected),
        color: C.navy,
        bg: '#ede9fe',
        icon: '~',
      },
      {
        label: 'COLLECTED',
        value: fmtK(collected),
        sub: fmtUSD(collected),
        color: C.green,
        bg: C.greenBg,
        icon: '+',
      },
      {
        label: 'OUTSTANDING',
        value: fmtK(outstanding),
        sub: fmtUSD(outstanding),
        color: C.red,
        bg: C.redBg,
        icon: '-',
      },
    ];

    return (
      <div style={{ padding: '16px 24px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 12,
          }}
        >
          {cards.map((c) => (
            <div
              key={c.label}
              style={{
                background: c.bg,
                borderRadius: 10,
                padding: '16px 14px',
                textAlign: 'center',
                border: `1px solid ${C.border}`,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}
            >
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: c.color,
                  lineHeight: 1,
                }}
              >
                {c.value}
              </div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: C.mutedLight,
                  letterSpacing: 1,
                  marginTop: 6,
                  textTransform: 'uppercase',
                }}
              >
                {c.label}
              </div>
            </div>
          ))}
        </div>
        {/* Collection rate bar */}
        <div
          style={{
            marginTop: 12,
            background: '#f1f5f9',
            borderRadius: 8,
            padding: '8px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>
            Collection Rate
          </span>
          <div
            style={{
              flex: 1,
              background: '#e2e8f0',
              borderRadius: 4,
              height: 6,
            }}
          >
            <div
              style={{
                width: `${Math.min(rate, 100)}%`,
                height: '100%',
                borderRadius: 4,
                background: rateColor,
                transition: 'width .3s',
              }}
            />
          </div>
          <span
            style={{
              fontSize: 15,
              fontWeight: 800,
              color: rateColor,
              minWidth: 36,
              textAlign: 'right',
            }}
          >
            {rate}%
          </span>
        </div>
      </div>
    );
  }

  renderFilters() {
    const { groupFilter, statusFilter, search, sortBy } = this.state;

    // Compute counts from all data (pre-filter) so chips always show totals
    const allWithCalc = (this.props.memberships || []).map((r) => ({
      ...r,
      ...this.calcRow(r),
    }));
    const groupCounts = { all: allWithCalc.length, alpha: 0, global: 0 };
    const statusCounts = {
      all: allWithCalc.length,
      paid: 0,
      request: 0,
      unpaid: 0,
    };
    allWithCalc.forEach((r) => {
      if (r.group === 'alpha') groupCounts.alpha++;
      else groupCounts.global++;
      statusCounts[r.st]++;
    });

    const groupChips = [
      {
        id: 'all',
        label: 'All',
        count: groupCounts.all,
        color: C.navy,
        bg: '#f1f5f9',
        activeBg: C.navy,
        activeColor: '#fff',
      },
      {
        id: 'alpha',
        label: 'Alpha',
        count: groupCounts.alpha,
        color: '#6d28d9',
        bg: '#f5f3ff',
        activeBg: '#6d28d9',
        activeColor: '#fff',
      },
      {
        id: 'global',
        label: 'Global',
        count: groupCounts.global,
        color: '#0369a1',
        bg: '#f0f9ff',
        activeBg: '#0369a1',
        activeColor: '#fff',
      },
    ];

    const statusChips = [
      {
        id: 'all',
        label: 'All',
        count: statusCounts.all,
        color: C.navy,
        bg: '#f1f5f9',
        activeBg: C.navy,
        activeColor: '#fff',
      },
      {
        id: 'paid',
        label: 'Paid',
        count: statusCounts.paid,
        color: C.green,
        bg: C.greenBg,
        activeBg: C.green,
        activeColor: '#fff',
      },
      {
        id: 'request',
        label: 'Review',
        count: statusCounts.request,
        color: C.amber,
        bg: C.amberBg,
        activeBg: C.amber,
        activeColor: '#fff',
      },
      {
        id: 'unpaid',
        label: 'Unpaid',
        count: statusCounts.unpaid,
        color: C.red,
        bg: C.redBg,
        activeBg: C.red,
        activeColor: '#fff',
      },
    ];

    const chipStyle = (chip, active) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      padding: '5px 12px',
      borderRadius: 20,
      border: active ? 'none' : `1px solid ${C.border}`,
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
      background: active ? chip.activeBg : chip.bg,
      color: active ? chip.activeColor : chip.color,
      transition: 'all .15s',
    });

    const countBadge = (chip, active) => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 18,
      height: 18,
      borderRadius: 9,
      fontSize: 12,
      fontWeight: 700,
      background: active ? 'rgba(255,255,255,0.25)' : `${chip.color}18`,
      color: active ? chip.activeColor : chip.color,
    });

    const selStyle = {
      padding: '7px 10px',
      borderRadius: 6,
      border: `1px solid ${C.border}`,
      fontSize: 14,
      background: C.card,
      color: C.text,
      outline: 'none',
      cursor: 'pointer',
    };

    return (
      <div style={{ padding: '0 24px 14px' }}>
        {/* Group chips */}
        <div
          style={{
            display: 'flex',
            gap: 6,
            marginBottom: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {groupChips.map((chip) => {
            const active = groupFilter === chip.id;
            return (
              <button
                key={chip.id}
                onClick={() => this.setState({ groupFilter: chip.id })}
                style={chipStyle(chip, active)}
              >
                {chip.label}
                <span style={countBadge(chip, active)}>{chip.count}</span>
              </button>
            );
          })}
        </div>

        {/* Status chips */}
        <div
          style={{
            display: 'flex',
            gap: 6,
            marginBottom: 10,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {statusChips.map((chip) => {
            const active = statusFilter === chip.id;
            return (
              <button
                key={chip.id}
                onClick={() => this.setState({ statusFilter: chip.id })}
                style={chipStyle(chip, active)}
              >
                {chip.label}
                <span style={countBadge(chip, active)}>{chip.count}</span>
              </button>
            );
          })}
        </div>

        {/* Sort + Search row */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Sort icon button with popup */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() =>
                this.setState((p) => ({ sortMenuOpen: !p.sortMenuOpen }))
              }
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                background: this.state.sortMenuOpen ? C.navy : C.card,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all .15s',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke={this.state.sortMenuOpen ? C.gold : C.muted}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="14" y2="12" />
                <line x1="4" y1="18" x2="8" y2="18" />
              </svg>
            </button>

            {this.state.sortMenuOpen && (
              <>
                <div
                  onClick={() => this.setState({ sortMenuOpen: false })}
                  style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 40,
                    left: 0,
                    zIndex: 100,
                    background: C.card,
                    borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    border: `1px solid ${C.border}`,
                    minWidth: 160,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      padding: '8px 12px',
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.mutedLight,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                    }}
                  >
                    Sort by
                  </div>
                  {[
                    { id: 'status', label: 'Status', icon: '\u25CF' },
                    { id: 'balance', label: 'Balance', icon: '\u2193' },
                    { id: 'amount', label: 'Amount', icon: '$' },
                    { id: 'name', label: 'Name A-Z', icon: 'A' },
                  ].map((opt) => {
                    const active = sortBy === opt.id;
                    return (
                      <div
                        key={opt.id}
                        onClick={() =>
                          this.setState({ sortBy: opt.id, sortMenuOpen: false })
                        }
                        style={{
                          padding: '9px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          cursor: 'pointer',
                          fontSize: 14,
                          fontWeight: 600,
                          color: active ? C.navy : C.muted,
                          background: active ? '#f1f5f9' : 'transparent',
                          transition: 'background .1s',
                        }}
                        onMouseEnter={(e) => {
                          if (!active)
                            e.currentTarget.style.background = '#f8fafc';
                        }}
                        onMouseLeave={(e) => {
                          if (!active)
                            e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <span
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: 6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 10,
                            fontWeight: 800,
                            background: active ? `${C.navy}` : '#f1f5f9',
                            color: active ? C.gold : C.muted,
                          }}
                        >
                          {opt.icon}
                        </span>
                        {opt.label}
                        {active && (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke={C.green}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ marginLeft: 'auto' }}
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div style={{ position: 'relative', flex: 1, minWidth: 140 }}>
            <input
              placeholder="Search by name or ID..."
              value={search}
              onChange={(e) => this.setState({ search: e.target.value })}
              style={{
                ...selStyle,
                width: '100%',
                paddingRight: 28,
                boxSizing: 'border-box',
              }}
            />
            {search && (
              <span
                onClick={() => this.setState({ search: '' })}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  color: '#9ca3af',
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                &times;
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderListHeader() {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '36px 1fr auto 64px 64px',
          padding: '8px 16px',
          fontSize: 12,
          fontWeight: 700,
          color: C.mutedLight,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          borderBottom: `1px solid ${C.border}`,
          background: '#f8fafc',
          position: 'sticky',
          top: 0,
          zIndex: 2,
        }}
      >
        <div>ID</div>
        <div>Location</div>
        <div>Plan</div>
        <div style={{ textAlign: 'right' }}>Due</div>
        <div style={{ textAlign: 'right' }}>Paid</div>
      </div>
    );
  }

  renderRow(row) {
    const { expandedId, verified } = this.state;
    const isExpanded = expandedId === row.companyId;
    const canExpand = row.st !== 'paid' && !row.isClosed;
    const badge = PLAN_BADGE[row.plan] || PLAN_BADGE.Basic;
    const stCfg = row.isClosed
      ? { label: 'CLOSED', color: C.muted, bg: '#f1f5f9' }
      : STATUS_LABEL[row.st];

    return (
      <div key={row.companyId}>
        {/* Main row */}
        <div
          onClick={() =>
            canExpand &&
            this.setState({ expandedId: isExpanded ? null : row.companyId })
          }
          style={{
            display: 'grid',
            gridTemplateColumns: '36px 1fr auto 64px 64px',
            alignItems: 'center',
            padding: '10px 16px',
            cursor: canExpand ? 'pointer' : 'default',
            background: isExpanded ? '#f8fafc' : C.card,
            borderBottom: `1px solid ${isExpanded ? 'transparent' : '#f0f0f0'}`,
            transition: 'background .15s',
            opacity: row.isClosed ? 0.6 : 1,
          }}
          onMouseEnter={(e) => {
            if (canExpand) e.currentTarget.style.background = '#f8fafc';
          }}
          onMouseLeave={(e) => {
            if (!isExpanded) e.currentTarget.style.background = C.card;
          }}
        >
          {/* ID */}
          <div style={{ fontSize: 12, color: C.mutedLight, fontWeight: 600 }}>
            {row.id}
          </div>

          {/* Location name */}
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: row.isClosed ? C.muted : C.navy,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              paddingRight: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {row.name}
            {row.isClosed && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  padding: '1px 6px',
                  borderRadius: 4,
                  letterSpacing: 0.3,
                  background: C.redBg,
                  color: C.red,
                  flexShrink: 0,
                }}
              >
                Closed
              </span>
            )}
          </div>

          {/* Plan badge */}
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              padding: '3px 8px',
              borderRadius: 4,
              background: badge.bg,
              color: badge.color,
              border: `1px solid ${badge.border}`,
              letterSpacing: 0.3,
            }}
          >
            {row.plan || 'N/A'}
          </div>

          {/* Due */}
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: C.navy,
              textAlign: 'right',
            }}
          >
            {fmtUSD(row.proj)}
          </div>

          {/* Paid */}
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: stCfg.color,
              textAlign: 'right',
            }}
          >
            {fmtUSD(row.coll)}
          </div>
        </div>

        {/* Expanded panel — REQUEST with Zelle */}
        {isExpanded && row.st === 'request' && row.zelle && (
          <div
            style={{
              margin: '0 16px 8px',
              borderRadius: 10,
              overflow: 'hidden',
              border: `1px solid #fde68a`,
            }}
          >
            <div style={{ background: '#fffbeb', padding: '14px 16px' }}>
              {verified === row.companyId ? (
                <div style={{ textAlign: 'center', padding: 10 }}>
                  <div style={{ fontSize: 20, marginBottom: 4 }}>&#10003;</div>
                  <div
                    style={{ color: C.green, fontWeight: 700, fontSize: 14 }}
                  >
                    Verified & Activated
                  </div>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#92400e',
                      marginBottom: 10,
                    }}
                  >
                    Zelle Payment — Pending Verification
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '8px 16px',
                      fontSize: 12,
                    }}
                  >
                    <div>
                      <span
                        style={{
                          color: '#92400e',
                          fontWeight: 600,
                          fontSize: 12,
                          textTransform: 'uppercase',
                        }}
                      >
                        Sender
                      </span>
                      <div style={{ fontWeight: 600, color: C.text }}>
                        {row.zelle.sender || '—'}
                      </div>
                    </div>
                    <div>
                      <span
                        style={{
                          color: '#92400e',
                          fontWeight: 600,
                          fontSize: 12,
                          textTransform: 'uppercase',
                        }}
                      >
                        Amount
                      </span>
                      <div style={{ fontWeight: 600, color: C.text }}>
                        ${row.zelle.sent}{' '}
                        <span style={{ color: C.muted }}>
                          / ${row.proj} required
                        </span>
                      </div>
                    </div>
                    <div>
                      <span
                        style={{
                          color: '#92400e',
                          fontWeight: 600,
                          fontSize: 12,
                          textTransform: 'uppercase',
                        }}
                      >
                        Confirmation #
                      </span>
                      <div style={{ fontWeight: 600, color: C.text }}>
                        {row.zelle.conf || '—'}
                      </div>
                    </div>
                    <div>
                      <span
                        style={{
                          color: '#92400e',
                          fontWeight: 600,
                          fontSize: 12,
                          textTransform: 'uppercase',
                        }}
                      >
                        Email / Phone
                      </span>
                      <div style={{ fontWeight: 600, color: C.text }}>
                        {row.zelle.email || '—'}
                      </div>
                    </div>
                    {row.zelle.note && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <span
                          style={{
                            color: '#92400e',
                            fontWeight: 600,
                            fontSize: 12,
                            textTransform: 'uppercase',
                          }}
                        >
                          Note
                        </span>
                        <div style={{ fontWeight: 500, color: C.text }}>
                          {row.zelle.note}
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                    {Number(row.zelle.sent || 0) >= row.proj ? (
                      <button
                        onClick={() => this.handleVerify(row)}
                        style={{
                          background: C.green,
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          padding: '8px 20px',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                          letterSpacing: 0.3,
                        }}
                      >
                        Verify & Activate
                      </button>
                    ) : (
                      <div
                        style={{
                          fontSize: 12,
                          color: C.red,
                          fontWeight: 600,
                          padding: '8px 0',
                        }}
                      >
                        Full payment of ${row.proj} required to activate.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Expanded — REQUEST without Zelle (Collect Later) */}
        {isExpanded && row.st === 'request' && !row.zelle && (
          <div
            style={{
              margin: '0 16px 8px',
              borderRadius: 10,
              overflow: 'hidden',
              border: `1px solid #fde68a`,
            }}
          >
            <div style={{ background: '#fffbeb', padding: '14px 16px' }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#92400e',
                  marginBottom: 8,
                }}
              >
                Collect Later — Pending Collection
              </div>
              <div style={{ fontSize: 12 }}>
                <strong>Amount Due:</strong>{' '}
                <span style={{ color: C.red }}>{fmtUSD(row.bal)}</span>
              </div>
              {this.props.updateLicensePayment && row.latestPaymentId && (
                <button
                  onClick={() => this.handleVerify(row)}
                  style={{
                    marginTop: 10,
                    background: C.green,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '8px 20px',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Mark as Paid
                </button>
              )}
            </div>
          </div>
        )}

        {/* Expanded — UNPAID */}
        {isExpanded && row.st === 'unpaid' && (
          <div
            style={{
              margin: '0 16px 8px',
              borderRadius: 10,
              overflow: 'hidden',
              border: `1px solid #fecaca`,
            }}
          >
            <div
              style={{
                background: '#fef2f2',
                padding: '14px 16px',
                fontSize: 12,
              }}
            >
              {verified === row.companyId ? (
                <div style={{ textAlign: 'center', padding: 10 }}>
                  <div style={{ fontSize: 20, marginBottom: 4 }}>&#10003;</div>
                  <div
                    style={{ color: C.green, fontWeight: 700, fontSize: 14 }}
                  >
                    {MONTHS[this.state.monthIdx]} {this.state.year} marked as
                    paid
                  </div>
                </div>
              ) : (
                <>
                  <div
                    style={{ fontWeight: 700, color: C.red, marginBottom: 8 }}
                  >
                    Unpaid — {fmtUSD(row.bal)} Outstanding
                  </div>
                  <div>
                    <strong>Payment Method:</strong> {formatPaymentMethod(row)}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <strong>Expires:</strong> {row.exp || '—'}
                  </div>
                  {row.zelle &&
                    Number(row.zelle.sent || 0) > 0 &&
                    Number(row.zelle.sent) < row.proj && (
                      <div
                        style={{
                          marginTop: 8,
                          color: '#92400e',
                          fontWeight: 600,
                        }}
                      >
                        Partial Zelle received: ${row.zelle.sent} of ${row.proj}
                        . ${row.proj - Number(row.zelle.sent)} more needed.
                      </div>
                    )}
                  {/* Mark as Paid — only enabled in monthly view. Quarterly /
                      yearly modes don't pin to a single cycle so the action
                      shows a guidance hint instead of a destructive button. */}
                  <div style={{ marginTop: 12 }}>
                    {this.state.periodMode === 'monthly' &&
                    this.props.markCyclePaid ? (
                      <button
                        onClick={() => this.handleMarkPaid(row)}
                        style={{
                          background: C.green,
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          padding: '8px 20px',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                          letterSpacing: 0.3,
                        }}
                      >
                        Mark {MONTHS[this.state.monthIdx]} {this.state.year} as
                        Paid
                      </button>
                    ) : this.props.markCyclePaid ? (
                      <div style={{ color: C.muted, fontStyle: 'italic' }}>
                        Switch to Monthly view to mark a specific cycle as paid.
                      </div>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  renderList() {
    const rows = this.getFilteredRows();
    const visible = rows.slice(0, this.state.visibleCount);

    return (
      <div
        style={{
          maxHeight: '50vh',
          overflowY: 'auto',
          background: C.card,
          margin: '0 24px 24px',
          borderRadius: 10,
          border: `1px solid ${C.border}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        {this.renderListHeader()}

        {visible.length === 0 && (
          <div
            style={{
              padding: 32,
              textAlign: 'center',
              color: C.muted,
              fontSize: 13,
            }}
          >
            No locations match your filters.
          </div>
        )}

        {visible.map((row) => this.renderRow(row))}

        {rows.length > this.state.visibleCount && (
          <div
            style={{
              textAlign: 'center',
              padding: 14,
              borderTop: `1px solid ${C.border}`,
            }}
          >
            <button
              onClick={() =>
                this.setState((p) => ({ visibleCount: p.visibleCount + 20 }))
              }
              style={{
                background: C.navy,
                color: C.gold,
                border: 'none',
                borderRadius: 8,
                padding: '7px 24px',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                letterSpacing: 0.3,
              }}
            >
              Load More ({rows.length - this.state.visibleCount} remaining)
            </button>
          </div>
        )}
      </div>
    );
  }

  render() {
    return (
      <div style={{ background: C.bg, borderRadius: 12, overflow: 'hidden' }}>
        {this.renderYearChips()}
        {this.renderPeriodSelector()}
        {this.renderKPIs()}
        {this.renderFilters()}
        {this.renderList()}
      </div>
    );
  }
}

export default MembershipSummary;
